import { client as baseClient } from '../client/client.gen';
import { refreshSessionApiAuthRefreshPost } from '../client/sdk.gen';
import { tokenStorage } from './token-storage';
import { dispatchUpgradeRequired } from './billing-events';
import type { ResolvedRequestOptions } from '../client/client/types.gen';

const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';

baseClient.setConfig({ baseUrl });

// Shared so simultaneous 401s (HTTP client and SSE streams) trigger one refresh.
let refreshPromise: Promise<boolean> | null = null;

export function refreshAccessToken(): Promise<boolean> {
  const refreshToken = tokenStorage.getRefreshToken();
  if (!refreshToken) {
    tokenStorage.clearTokens();
    return Promise.resolve(false);
  }

  if (!refreshPromise) {
    refreshPromise = (async () => {
      try {
        const refreshResponse = await refreshSessionApiAuthRefreshPost({
          client: baseClient,
          body: { refresh_token: refreshToken },
        });

        if (refreshResponse.data && refreshResponse.data.access_token) {
          tokenStorage.setTokens(refreshResponse.data.access_token, refreshResponse.data.refresh_token);
          return true;
        }
        tokenStorage.clearTokens();
        return false;
      } catch {
        tokenStorage.clearTokens();
        return false;
      } finally {
        refreshPromise = null;
      }
    })();
  }
  return refreshPromise;
}

/**
 * Simple request interceptor that adds the access token to requests.
 */
baseClient.interceptors.request.use((request) => {
  // Skip adding token for refresh endpoint to avoid sending expired tokens
  if (request.url.includes('/auth/refresh')) {
    return request;
  }

  const token = tokenStorage.getAccessToken();

  if (token) {
    request.headers.set('Authorization', `Bearer ${token}`);
  }

  return request;
});

type OptionsWithMeta = ResolvedRequestOptions & {
  meta?: {
    isRetry?: boolean;
    [key: string]: unknown;
  };
};

/**
 * Response interceptor to handle 401 errors and refresh token.
 */
baseClient.interceptors.response.use(async (response, request, options) => {
  if (response.status !== 401) {
    return response;
  }

  const opts = options as unknown as OptionsWithMeta;

  // Prevent infinite loops
  if (request.url.includes('/auth/refresh') || opts.meta?.isRetry) {
    return response;
  }

  try {
    const refreshSuccess = await refreshAccessToken();

    if (refreshSuccess) {
      // Retry the original request with the new token
      const result = await baseClient.request({
        ...options,
        meta: { ...(opts.meta || {}), isRetry: true },
        parseAs: 'stream',
        throwOnError: false,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any);

      // `result.response` is optional in the generated client (a request that
      // threw before receiving a response leaves it unset). Fall back to the
      // original 401 response rather than returning undefined.
      return result.response ?? response;
    } else {
      return response;
    }
  } catch {
    tokenStorage.clearTokens();
    return response;
  }
});

/**
 * Surface 402 UPGRADE_REQUIRED responses to the global upsell modal. Registered
 * after the 401 handler so the refresh interceptor stays first. The body is
 * cloned so the original response stream stays intact for the caller.
 */
baseClient.interceptors.response.use(async (response) => {
  if (response.status === 402) {
    try {
      const body = await response.clone().json();
      if (body?.error_code === 'UPGRADE_REQUIRED') {
        dispatchUpgradeRequired({
          feature: body.feature ?? '',
          requiredPlan: body.required_plan ?? null,
          ownerType: body.owner_type ?? '',
          ownerId: body.owner_id ?? '',
          message: body.detail ?? '',
        });
      }
    } catch {
      // Non-JSON 402 — ignore; the caller still receives the raw response.
    }
  }
  return response;
});

export const httpClient = baseClient;
